import {
  Box,
  Grid,
  Link,
  Stack,
  Typography,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Button,
} from "@mui/material";
import { PagePaper, SectionPaper } from "@/components/Paper";
import { PageTitle } from "@/components/Title";
import { FeedGrid } from "../components/AllMattersDetailFeedGrid";
import { KeyboardArrowLeft } from "@mui/icons-material";
import React, { useState } from "react";
import { useApiClient } from "@/hooks/useApiClient";
import { useParams } from "react-router-dom";
import useSWR from "swr";
import { isRequestCompleted, statusToLabel } from "@/features/allMatters/utils";
import { type RequestDetailAndRealEstates } from "@/features/allMatters/types";
import CircularProgress from "@mui/material/CircularProgress";
import { useFeatureFlags } from "@/configs/featureFlag";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import { format } from "date-fns";
import { AllMattersEditModal } from "@/features/allMatters/components/AllMattersEditModal";
import { toast } from "react-toastify";

export const AllMattersDetail: React.FC = () => {
  const { apiClient } = useApiClient();
  const { id } = useParams();
  const { mypageTop } = useFeatureFlags();
  const [openAllMattersEditDialog, setOpenAllMattersEditDialog] =
    useState<boolean>(false);
  const fetcher = async (url: string): Promise<RequestDetailAndRealEstates> => {
    const { data } = await apiClient.get<RequestDetailAndRealEstates>(url);
    return data;
  };

  const { data, mutate } = useSWR<RequestDetailAndRealEstates>(
    id ? `/all-matters-analyze/${id}` : null,
    fetcher,
    { revalidateOnFocus: true, refreshInterval: 60000, dedupingInterval: 30000 }
  );
  if (data == null) {
    return <CircularProgress />;
  }

  const datetimeFormatter = (datetime: string): string => {
    return format(new Date(datetime), "yyyy-MM-dd HH:mm");
  };

  // 識別名を更新後、すぐにデータの再フェッチを行う
  const handleRefreshAfterEdit = (): void => {
    mutate(data).catch(() => {
      toast.error("データの再取得に失敗しました。");
    });
  };

  return (
    <PagePaper>
      <form>
        <Grid container sx={{ mb: 2 }} rowSpacing={1}>
          <Grid item xs={12}>
            <Grid container>
              <Grid item xs={12}>
                {/* ページタイトル */}
                <Box sx={{ display: "flex", mb: 3 }}>
                  <LibraryBooksIcon sx={{ mr: 1 }} fontSize="large" />
                  <PageTitle>全部事項データ化詳細（テスト版）</PageTitle>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Table size="small" sx={{ mb: 2, ml: 1 }}>
                  <TableBody>
                    <TableRow>
                      <TableCell
                        component="th"
                        sx={{
                          width: "10%",
                          border: "none",
                          p: 1,
                          pl: 0,
                          pr: 0,
                        }}
                      >
                        ステータス
                      </TableCell>
                      <TableCell sx={{ border: "none", p: 1, pl: 0 }}>
                        {statusToLabel(data?.status)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        component="th"
                        sx={{
                          width: "10%",
                          border: "none",
                          p: 1,
                          pl: 0,
                          pr: 0,
                        }}
                      >
                        件数
                      </TableCell>
                      <TableCell sx={{ border: "none", p: 1, pl: 0 }}>
                        {data?.details.length}件（成功：
                        {
                          data?.details.filter(
                            (detail) => detail.status === "succeeded"
                          ).length
                        }
                        件、 失敗：
                        {
                          data?.details.filter(
                            (detail) => detail.status === "failed"
                          ).length
                        }
                        件）
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        component="th"
                        sx={{
                          width: "10%",
                          border: "none",
                          p: 1,
                          pl: 0,
                          pr: 0,
                        }}
                      >
                        識別番号
                      </TableCell>
                      <TableCell sx={{ border: "none", p: 1, pl: 0 }}>
                        {data?.taskId}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        component="th"
                        sx={{
                          width: "10%",
                          border: "none",
                          p: 1,
                          pl: 0,
                          pr: 0,
                        }}
                      >
                        識別名
                      </TableCell>
                      <TableCell sx={{ border: "none", p: 1, pl: 0 }}>
                        {data?.identificationName}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        component="th"
                        sx={{
                          width: "10%",
                          border: "none",
                          p: 1,
                          pl: 0,
                          pr: 0,
                        }}
                      >
                        依頼日時
                      </TableCell>
                      <TableCell sx={{ border: "none", p: 1, pl: 0 }}>
                        {datetimeFormatter(data?.createdAt)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Grid>
              <Grid item xs={12} sx={{ textAlign: "left" }}>
                <Button
                  variant="outlined"
                  component={Link}
                  onClick={() => {
                    setOpenAllMattersEditDialog(true);
                  }}
                  sx={{
                    textAlign: "center",
                    fontWeight: "bold",
                  }}
                >
                  識別名編集
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <SectionPaper>
          <FeedGrid
            row={data?.details}
            requestId={data?.id}
            disabledAction={
              !isRequestCompleted(data?.status) || data?.succeededCount === 0
            }
          />
          <Box sx={{ mb: 6 }} />
          <Stack
            direction={"row"}
            spacing={4}
            justifyContent={"center"}
            mb={2}
          ></Stack>
        </SectionPaper>

        {/* 戻る */}
        <Box sx={{ mt: 2 }}>
          <Link href="/allmatters/list" sx={{ display: "inline-block" }}>
            <Box sx={{ display: "flex" }}>
              <KeyboardArrowLeft />
              <Typography>一覧に戻る</Typography>
            </Box>
          </Link>
        </Box>
      </form>
      {openAllMattersEditDialog && (
        <AllMattersEditModal
          open={openAllMattersEditDialog}
          setOpen={setOpenAllMattersEditDialog}
          handleClose={handleRefreshAfterEdit}
          pictureRequestId={Number(id)}
          data={data}
        />
      )}
    </PagePaper>
  );
};
